import React, { useContext, useMemo, useRef, useState } from 'react'
import { Button, Col, Container, Input, Table, UncontrolledCollapse } from 'reactstrap'

import './FindAdminsPage.css'

import { API } from 'API'
import { RequestToasterContext } from 'containers/Providers'
import { AccessCountries } from 'domains/user'
import { AdminMatch } from 'domains/user/AdminMatch'
import { DepartmentSuperAdmins } from 'domains/user/DepartmentSuperAdmins'
import { ALL_ADMIN_DEPARTMENTS } from 'domains/user/UserAdminDepartments'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { StandardErrorResponse } from 'types/APIResponses'

import {
  CountryOption,
  euCountryCodes,
  flattenCountries,
  resolveEuState,
  toggleEuRegion,
  toggleSelectAll,
  toggleValue,
} from './findAdminsSelection'

const NO_RESULTS_MESSAGE =
  'No responsible person found. Contact the Super Admin or change the search criteria.'

const EuRegionCheckbox = ({
  state,
  onChange,
}: {
  state: 'checked' | 'unchecked' | 'indeterminate'
  onChange: (checked: boolean) => void
}) => {
  const ref = useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.indeterminate = state === 'indeterminate'
    }
  }, [state])

  return (
    <input
      type="checkbox"
      className="form-check-input"
      ref={ref}
      checked={state === 'checked'}
      onChange={(event) => onChange(event.target.checked)}
      aria-label="EU region"
    />
  )
}

export const FindAdminsPage = () => {
  const { requestStatusRef } = useContext(RequestToasterContext)

  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([])
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set())
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set())
  const [countrySearch, setCountrySearch] = useState('')
  const [departmentSearch, setDepartmentSearch] = useState('')
  const [results, setResults] = useState<AdminMatch[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [departmentSuperAdmins, setDepartmentSuperAdmins] = useState<DepartmentSuperAdmins[]>([])

  useEffectOnce(() => {
    API.getCountriesPermissions()
      .then((accessCountries: AccessCountries) => {
        setCountryOptions(flattenCountries(accessCountries))
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  })

  useEffectOnce(() => {
    API.getDepartmentSuperAdmins()
      .then((response) => {
        setDepartmentSuperAdmins(response)
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  })

  const euCodes = useMemo(() => euCountryCodes(countryOptions), [countryOptions])
  const euState = resolveEuState(euCodes, selectedCountries)

  const nonEuOptions = countryOptions.filter((option) => !option.isEu)
  const euOptions = countryOptions.filter((option) => option.isEu)

  const matchesCountrySearch = (option: CountryOption) =>
    option.name.toLowerCase().includes(countrySearch.trim().toLowerCase())

  const filteredNonEuOptions = nonEuOptions.filter(matchesCountrySearch)
  const filteredEuOptions = euOptions.filter(matchesCountrySearch)

  const filteredDepartments = ALL_ADMIN_DEPARTMENTS.filter((department) =>
    department.toLowerCase().includes(departmentSearch.trim().toLowerCase()),
  )

  const allCountryCodes = countryOptions.map((option) => option.isoCode)

  const hasResults = results != null
  const hasAnyAdmin = results?.some((match) => match.admins.length > 0) ?? false

  const onToggleCountry = (isoCode: string) =>
    setSelectedCountries((prev) => toggleValue(isoCode, prev))

  const onToggleDepartment = (department: string) =>
    setSelectedDepartments((prev) => toggleValue(department, prev))

  const onSelectAllCountries = () =>
    setSelectedCountries((prev) => toggleSelectAll(allCountryCodes, prev))

  const onSelectAllDepartments = () =>
    setSelectedDepartments((prev) => toggleSelectAll([...ALL_ADMIN_DEPARTMENTS], prev))

  const onToggleEuRegion = (checked: boolean) =>
    setSelectedCountries((prev) => toggleEuRegion(euCodes, prev, checked))

  const onSearch = () => {
    setIsSearching(true)
    API.findAdmins([...selectedCountries], [...selectedDepartments])
      .then((response) => {
        setResults(response)
      })
      .catch((error: StandardErrorResponse) => {
        setResults([])
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
      .finally(() => setIsSearching(false))
  }

  const renderCountryOption = (option: CountryOption, isChild = false) => (
    <div key={option.isoCode} className={`form-check${isChild ? ' pl-4' : ''}`}>
      <input
        type="checkbox"
        className="form-check-input"
        id={`country-${option.isoCode}`}
        checked={selectedCountries.has(option.isoCode)}
        onChange={() => onToggleCountry(option.isoCode)}
      />
      <label className="form-check-label" htmlFor={`country-${option.isoCode}`}>
        {option.name}
      </label>
    </div>
  )

  return (
    <Container fluid>
      <div className="find-admins-header">
        <h2>Find the Admins</h2>
        <p className="find-admins-subtitle">Search by Country and Department</p>
      </div>
      <hr />

      <div className="find-admins-grid">
        <Col className="find-admins-country-area">
          <div className="find-admins-filter-header">
            <h5>Country</h5>
            <Button
              color="primary"
              size="sm"
              className="find-admins-select-all"
              onClick={onSelectAllCountries}>
              Select All
            </Button>{' '}
          </div>
          <Input
            type="text"
            placeholder="Search"
            aria-label="Search countries"
            value={countrySearch}
            onChange={(event) => setCountrySearch(event.target.value)}
            className="mb-2"
          />
          <div className="find-admins-list p-2 d-flex flex-column">
            {filteredEuOptions.length > 0 && (
              <>
                <div className="form-check">
                  <EuRegionCheckbox state={euState} onChange={onToggleEuRegion} />
                  <label className="form-check-label" htmlFor="eu-region-toggler">
                    EU region
                  </label>
                  <a className="text-secondary dropdown-toggle p-2" id="eu-region-toggler"></a>
                </div>
                <UncontrolledCollapse toggler="#eu-region-toggler">
                  <div className="pl-2">
                    {filteredEuOptions.map((option) => renderCountryOption(option, true))}
                  </div>
                </UncontrolledCollapse>
              </>
            )}

            {filteredNonEuOptions.map((option) => renderCountryOption(option))}
          </div>
        </Col>

        <Col className="find-admins-department-area">
          <div className="find-admins-filter-header">
            <h5>Department</h5>
            <Button
              color="primary"
              size="sm"
              className="find-admins-select-all"
              onClick={onSelectAllDepartments}>
              Select All
            </Button>
          </div>
          <Input
            type="text"
            placeholder="Search"
            aria-label="Search departments"
            value={departmentSearch}
            onChange={(event) => setDepartmentSearch(event.target.value)}
            className="mb-2"
          />
          <div className="find-admins-list p-2 d-flex flex-column">
            {filteredDepartments.map((department) => (
              <div key={department} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`department-${department}`}
                  checked={selectedDepartments.has(department)}
                  onChange={() => onToggleDepartment(department)}
                />
                <label className="form-check-label" htmlFor={`department-${department}`}>
                  {department}
                </label>
              </div>
            ))}
          </div>

          <Button
            color="primary"
            className="mt-3"
            onClick={onSearch}
            disabled={
              isSearching || (selectedCountries.size === 0 && selectedDepartments.size === 0)
            }>
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </Col>

        <Col className="find-admins-preview find-admins-preview-area">
          <Table bordered className="find-admins-results">
            <thead>
              <tr>
                <th>Department</th>
                <th>Super Admin (All Countries)</th>
              </tr>
            </thead>
            <tbody>
              {departmentSuperAdmins.map((entry) => (
                <tr key={entry.department}>
                  <td>{entry.department}</td>
                  <td>
                    {entry.superAdmins.length === 0 ? (
                      <span className="find-admins-none">None</span>
                    ) : (
                      entry.superAdmins.map((admin) => <div key={admin}>{admin}</div>)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>

        <Col className="find-admins-results-row find-admins-results-area">
          {hasResults && !hasAnyAdmin && (
            <div className="alert alert-warning" role="alert">
              {NO_RESULTS_MESSAGE}
            </div>
          )}

          {hasResults && hasAnyAdmin && (
            <Table bordered className="find-admins-results">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Department</th>
                  <th>Admin</th>
                </tr>
              </thead>
              <tbody>
                {results.map((match) => (
                  <tr key={`${match.countryName}-${match.department}`}>
                    <td>{match.countryName}</td>
                    <td>{match.department}</td>
                    <td>
                      {match.admins.length === 0 ? (
                        <span className="find-admins-none">None</span>
                      ) : (
                        match.admins.map((admin) => <div key={admin}>{admin}</div>)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </div>
    </Container>
  )
}
